// G2.26 — Sync Health Overview widget (Platform dashboard)
//
// Cross-tenant pending-conflict roll-up for platform staff
// (SUPER_ADMIN / ADMIN / MODERATOR). Each row is a tenant with at least
// one PENDING sync conflict, showing the count and the most-recent
// conflict timestamp. Click-through navigates to the per-tenant Sync
// Health page pre-scoped via `?tenant=<id>` (handled by G2.24 page).
//
// Data source: GET /sync/conflict-summary (adminSyncService). Backend
// enforces role gate — OWNER/MANAGER get 403. We additionally gate at the
// component level so tenant-level users never see an empty widget if this
// somehow slips into a tenant dashboard.
//
// Visual: matches the PlatformDashboardContent SectionCard shell exactly
// (same GLASS_PANEL, same eyebrow typography, same hover transition) so
// the widget drops into the existing grid without visual seams.
//
// Traceability: G2.26, FR-SYNC-013, US-BILL-019.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, EmptyState, Skeleton, Spinner } from '@bbpos/ui';
import { GLASS, cn, toneClasses } from '@/constants/design-tokens';
import { useAuthStore } from '@/stores/use-auth-store';
import {
  adminSyncService,
  type SyncConflictSummaryRow,
} from '@/services/sync.service';
import { connectSocket, disconnectSocket, onSocketEvent } from '@/lib/socket-client';

// ─── Constants ──────────────────────────────────────────────────────────────

const PLATFORM_ROLES = new Set(['SUPER_ADMIN', 'ADMIN', 'MODERATOR']);
const GRID_COLS = 'grid-cols-[minmax(0,1fr)_96px_140px_24px]';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5-minute fallback refresh

// ─── Types ───────────────────────────────────────────────────────────────────

type SortKey = 'pending_count' | 'tenant_name' | 'last_conflict_at';
type SortDir = 'asc' | 'desc';

interface SortState {
  key: SortKey;
  dir: SortDir;
}

// ─── Formatters ──────────────────────────────────────────────────────────────

// All cron / display text uses Asia/Kolkata per Orio360 convention.
const dtFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
  timeZone: 'Asia/Kolkata',
});

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : dtFormatter.format(d);
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

// ─── Sort helper ─────────────────────────────────────────────────────────────

function sortRows(rows: SyncConflictSummaryRow[], sort: SortState): SyncConflictSummaryRow[] {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    let cmp = 0;
    if (sort.key === 'pending_count') {
      cmp = a.pending_count - b.pending_count;
    } else if (sort.key === 'tenant_name') {
      cmp = a.tenant_name.localeCompare(b.tenant_name);
    } else {
      cmp = new Date(a.last_conflict_at).getTime() - new Date(b.last_conflict_at).getTime();
    }
    return sort.dir === 'asc' ? cmp : -cmp;
  });
  return sorted;
}

// ─── Widget ──────────────────────────────────────────────────────────────────

export function SyncHealthOverviewWidget() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);

  const isPlatformUser = useMemo(
    () => PLATFORM_ROLES.has(profile?.role ?? ''),
    [profile?.role],
  );

  const [rows, setRows] = useState<SyncConflictSummaryRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ key: 'pending_count', dir: 'desc' });

  // ── Load ──
  const load = useCallback(async () => {
    if (!isPlatformUser) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await adminSyncService.getConflictSummary();
      setRows(res.data);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load sync health summary');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [isPlatformUser]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Real-time: subscribe to conflict + resolution events (G2.25) ──
  // The widget auto-refreshes its summary when any conflict lands or is
  // resolved, so platform staff always see up-to-date counts.
  // Fallback: 5-minute polling interval in case WebSocket is unreliable.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!isPlatformUser) return;
    connectSocket();

    const unsub1 = onSocketEvent('sync:conflict:platform', () => {
      void loadRef.current();
    });
    const unsub2 = onSocketEvent('sync:conflict:resolved:platform', () => {
      void loadRef.current();
    });

    const intervalId = setInterval(() => {
      void loadRef.current();
    }, POLL_INTERVAL_MS);

    return () => {
      unsub1();
      unsub2();
      clearInterval(intervalId);
      disconnectSocket();
    };
  }, [isPlatformUser]);

  // ── Derived ──
  const sortedRows = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const totalPending = useMemo(
    () => rows.reduce((sum, r) => sum + r.pending_count, 0),
    [rows],
  );
  const worstOffender = useMemo(
    () => rows.reduce<SyncConflictSummaryRow | null>(
      (worst, r) => (worst && worst.pending_count >= r.pending_count ? worst : r),
      null,
    ),
    [rows],
  );

  // ── Handlers ──
  const handleSort = useCallback((key: SortKey) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const handleDrillThrough = useCallback(
    (tenantId: string) => {
      navigate(`/sync-health?tenant=${encodeURIComponent(tenantId)}`);
    },
    [navigate],
  );

  // ── Render gates ──
  // Hide completely for tenant-level users; they have their own Sync Health
  // page without a cross-tenant aggregate.
  if (!isPlatformUser) return null;

  const dangerTone = toneClasses('danger');

  return (
    <section
      className={cn(
        GLASS,
        'relative overflow-hidden transition-shadow duration-200 hover:shadow-lg',
      )}
    >
      {/* Header — amber accent strip signals a "conflict / attention" surface */}
      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-amber-500/18 to-amber-500/0 pointer-events-none" />
      <div className="relative flex flex-wrap items-start justify-between gap-4 px-5 pt-4 pb-5">
        <div className="min-w-0">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
            Platform health
          </p>
          <h2 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
            Sync Health Overview
          </h2>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Tenants with unresolved offline-sync conflicts. Click a row to inspect and resolve.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-amber-50 dark:bg-amber-900/30 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300 tabular-nums">
            {totalPending} pending · {rows.length} tenants
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={isLoading}
            className="rounded-full bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm px-3 py-1.5 text-sm font-medium text-neutral-600 dark:text-neutral-300 shadow-sm transition-all duration-150 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="relative px-5 pt-1 pb-4">
        {/* Loading overlay for re-fetches (data already visible) */}
        {isLoading && rows.length > 0 && (
          <div className="absolute inset-0 bg-white/30 dark:bg-neutral-900/30 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-2xl transition-opacity">
            <Spinner size="sm" />
          </div>
        )}

        {error && (
          <div className="mb-4">
            <Alert variant="danger" title="Sync Health Overview error" description={error} />
          </div>
        )}

        {isLoading && rows.length === 0 ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rect" height={48} className="rounded-xl" />
            ))}
          </div>
        ) : sortedRows.length === 0 && !error ? (
          <EmptyState
            variant="section"
            icon={<div className="h-12 w-12 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center"><CheckIcon /></div>}
            title="All tenants are in sync"
            description="No pending offline-sync conflicts across the platform. The widget will light up the moment a conflict lands."
          />
        ) : (
          <div className="space-y-3">
            {/* Worst-offender callout — only when there's a clear leader */}
            {worstOffender && worstOffender.pending_count >= 5 && (
              <button
                type="button"
                onClick={() => handleDrillThrough(worstOffender.tenant_id)}
                className={cn(
                  'w-full text-left rounded-2xl p-4 transition-all duration-150',
                  `bg-gradient-to-r ${dangerTone.accent} hover:from-rose-500/15 hover:via-rose-500/8`,
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className={cn('text-[11px] font-semibold uppercase tracking-[0.18em]', dangerTone.badge.split(' ').filter(c => c.startsWith('text-')).join(' '))}>
                      Top offender
                    </p>
                    <p className="mt-1 truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {worstOffender.tenant_name}
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                      Last conflict {formatRelative(worstOffender.last_conflict_at)} ·{' '}
                      <span className="font-mono">{formatDateTime(worstOffender.last_conflict_at)}</span>
                    </p>
                  </div>
                  <div className={cn('flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl', dangerTone.icon)}>
                    <span className="text-2xl font-semibold tabular-nums">
                      {worstOffender.pending_count}
                    </span>
                  </div>
                </div>
              </button>
            )}

            {/* Sortable list */}
            <div className="overflow-hidden rounded-2xl" role="grid" aria-label="Tenants with pending sync conflicts">
              <div className={cn('grid items-center gap-3 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-400 dark:text-neutral-500', GRID_COLS)} role="row">
                <SortHeader
                  label="Tenant"
                  active={sort.key === 'tenant_name'}
                  dir={sort.dir}
                  onClick={() => handleSort('tenant_name')}
                />
                <SortHeader
                  label="Pending"
                  active={sort.key === 'pending_count'}
                  dir={sort.dir}
                  onClick={() => handleSort('pending_count')}
                  align="right"
                />
                <SortHeader
                  label="Last Conflict"
                  active={sort.key === 'last_conflict_at'}
                  dir={sort.dir}
                  onClick={() => handleSort('last_conflict_at')}
                  align="right"
                />
                <span role="columnheader" />
              </div>

              <div className="space-y-1.5">
                {sortedRows.map((row) => (
                  <button
                    key={row.tenant_id}
                    type="button"
                    role="row"
                    onClick={() => handleDrillThrough(row.tenant_id)}
                    className={cn('group grid w-full items-center gap-3 rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 px-4 py-3 text-left transition-colors hover:bg-neutral-100/80 dark:hover:bg-neutral-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40', GRID_COLS)}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        {row.tenant_name}
                      </p>
                      <p className="mt-0.5 truncate font-mono text-[10px] text-neutral-400 dark:text-neutral-500">
                        {row.tenant_id}
                      </p>
                    </div>
                    <div className="text-right">
                      <SeverityPill count={row.pending_count} />
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xs font-medium text-neutral-700 dark:text-neutral-200 tabular-nums">
                        {formatDateTime(row.last_conflict_at)}
                      </p>
                      <p className="text-[10px] text-neutral-400 dark:text-neutral-500">
                        {formatRelative(row.last_conflict_at)}
                      </p>
                    </div>
                    <span className="flex h-6 w-6 items-center justify-center text-neutral-300 transition-colors group-hover:text-neutral-600 dark:text-neutral-600 dark:group-hover:text-neutral-200">
                      <ChevronRightIcon />
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <p className="pt-2 text-center text-[11px] text-neutral-400 dark:text-neutral-500">
              Showing top {sortedRows.length} tenant{sortedRows.length === 1 ? '' : 's'} with pending
              conflicts. Full page:{' '}
              <button
                type="button"
                onClick={() => navigate('/sync-health')}
                className="font-semibold text-brand-600 hover:underline dark:text-brand-400"
              >
                Sync Health →
              </button>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SortHeader({
  label,
  active,
  dir,
  onClick,
  align = 'left',
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  return (
    <button
      type="button"
      role="columnheader"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1 transition-colors hover:text-neutral-600 dark:hover:text-neutral-300',
        align === 'right' && 'justify-end',
        active && 'text-neutral-700 dark:text-neutral-200',
      )}
    >
      <span>{label}</span>
      {active && (
        <span className="text-[9px]" aria-hidden>
          {dir === 'desc' ? '▼' : '▲'}
        </span>
      )}
    </button>
  );
}

function SeverityPill({ count }: { count: number }) {
  // Red for 10+, amber for 3-9, brand for 1-2
  if (count >= 10) {
    return (
      <Badge variant="danger" size="sm">
        <span className="font-mono tabular-nums">{count}</span>
      </Badge>
    );
  }
  if (count >= 3) {
    return (
      <Badge variant="warning" size="sm">
        <span className="font-mono tabular-nums">{count}</span>
      </Badge>
    );
  }
  return (
    <Badge variant="info" size="sm">
      <span className="font-mono tabular-nums">{count}</span>
    </Badge>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
